import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import moment from 'moment';
import currencyFormatter from '../../../utils/currencyFormatter';
import { notifyError } from '../../../utils/notify';
import {
  getBlocos,
  getClausulas,
  getModeloItens,
  getSecoes,
} from '../../../pages/GestaoMinutas/minutaApi';
import { montarArvoreNumerada } from '../../../pages/GestaoMinutas/minutaRender';
import {
  buildContrFornPlaceholderContext,
  renderMinutaBodyHtml,
} from './minutaPlaceholders';
import {
  getEntregavelPrazoDias,
  getValorPctFromEntregavel,
  hasEntregavelDtPrev,
} from './contrFornEntregaveisUtils';

const BLOCO_TIPO_LABEL = {
  preambulo: 'Preâmbulo',
  qualificacao: 'Qualificação',
  considerandos: 'Considerandos',
  fecho: 'Fecho',
  assinaturas: 'Assinaturas',
  testemunhas: 'Testemunhas',
  anexo: 'Anexo',
  outro: 'Outro',
};

const A4_WIDTH_PX = 794;
const A4_HEIGHT_MM = 297;
const A4_WIDTH_MM = 210;
const PAGE_MARGIN_MM = 12;

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const indexById = (rows, idKey) => Object.fromEntries(
  (rows || []).map((row) => [row[idKey], row]),
);

const formatMoney = (value) => currencyFormatter.format(Number(value || 0));

const renderBodyOrFallback = (body, placeholderContext) => {
  const html = renderMinutaBodyHtml(body, placeholderContext);
  if (!html) return '<p style="color:#6c757d;margin:0;">Sem conteúdo.</p>';
  return `<div style="font-size:14px;line-height:1.5;color:#212529;">${html}</div>`;
};

const buildSectionHtml = (title, bodyHtml, showTopDivider = false) => `
  <div style="margin-bottom:1.25rem;">
    ${showTopDivider ? '<div style="border-top:1px solid #e6e6e6;margin:0 0 1.25rem;"></div>' : ''}
    <h2 style="font-size:16px;font-weight:600;margin:0 0 0.75rem;">${escapeHtml(title)}</h2>
    ${bodyHtml}
  </div>
`;

const buildMinutaTreeHtml = ({
  arvore,
  blocoById,
  secById,
  clausById,
  placeholderContext,
}) => arvore.map((node, index) => {
  const { item, numero, filhos } = node;
  const showTopDivider = index > 0;

  if (item.minutaModItem_tipo === 'bloco') {
    const bloco = blocoById[item.minutaBloco_id];
    const tipoLabel = bloco
      ? (BLOCO_TIPO_LABEL[bloco.minutaBloco_tipo] || bloco.minutaBloco_tipo)
      : null;
    const title = bloco
      ? `Bloco — ${bloco.minutaBloco_nome}${tipoLabel ? ` (${tipoLabel})` : ''}`
      : 'Bloco — (bloco removido)';
    const body = bloco
      ? renderBodyOrFallback(bloco.minutaBloco_body, placeholderContext)
      : '<p style="color:#6c757d;margin:0;">Referência de bloco não encontrada no catálogo.</p>';
    return buildSectionHtml(title, body, showTopDivider);
  }

  if (item.minutaModItem_tipo === 'secao') {
    const sec = secById[item.minutaSec_id];
    const title = sec
      ? `Seção ${numero} — ${sec.minutaSec_nome}`
      : `Seção ${numero} — (seção removida)`;

    let filhosHtml = '<p style="color:#6c757d;margin:0;">Nenhuma cláusula nesta seção.</p>';
    if ((filhos || []).length > 0) {
      filhosHtml = (filhos || []).map((filho, filhoIdx) => {
        const cl = clausById[filho.item.minutaClaus_id];
        const filhoTitle = cl
          ? `Cláusula ${filho.numero} — ${cl.minutaClaus_nome}`
          : `Cláusula ${filho.numero} — (cláusula removida)`;
        const filhoBody = cl
          ? renderBodyOrFallback(cl.minutaClaus_body, placeholderContext)
          : '<p style="color:#6c757d;margin:0;">Referência de cláusula não encontrada no catálogo.</p>';
        return `
          <div style="margin:0 0 1rem 12px;">
            ${filhoIdx > 0 ? '<div style="border-top:1px solid #e6e6e6;margin:0 0 0.75rem;"></div>' : ''}
            <h3 style="font-size:14px;font-weight:600;margin:0 0 0.5rem;">${escapeHtml(filhoTitle)}</h3>
            ${filhoBody}
          </div>
        `;
      }).join('');
    }

    return buildSectionHtml(title, filhosHtml, showTopDivider);
  }

  const cl = clausById[item.minutaClaus_id];
  const title = cl
    ? `Cláusula ${numero} — ${cl.minutaClaus_nome}`
    : `Cláusula ${numero} — (cláusula removida)`;
  const body = cl
    ? renderBodyOrFallback(cl.minutaClaus_body, placeholderContext)
    : '<p style="color:#6c757d;margin:0;">Referência de cláusula não encontrada no catálogo.</p>';
  return buildSectionHtml(title, body, showTopDivider);
}).join('');

const buildEntregaveisTableHtml = (listaParcelas = []) => {
  const rows = (listaParcelas || []).filter(Boolean);
  if (rows.length === 0) {
    return '<p style="color:#6c757d;margin:0;">Nenhum entregável registrado.</p>';
  }

  const bodyRows = rows.map((item, index) => {
    const num = item.contrFornEvento_num || (index + 1);
    const desc = item.contrFornEvento_desc || '—';
    const prazoDias = getEntregavelPrazoDias(item);
    const prazo = prazoDias != null && prazoDias >= 0
      ? `${prazoDias} dias`
      : '—';
    const dataPrevista = hasEntregavelDtPrev(item)
      ? moment(item.contrFornEvento_dtPrev).format('DD/MM/YYYY')
      : '—';
    const pct = getValorPctFromEntregavel(item);
    const valor = formatMoney(item.contrFornEvento_vlr);
    const pctLabel = pct != null ? `${pct}%` : '—';

    return `
      <tr>
        <td style="border:1px solid #ced4da;padding:6px 8px;">${escapeHtml(num)}</td>
        <td style="border:1px solid #ced4da;padding:6px 8px;">${escapeHtml(desc)}</td>
        <td style="border:1px solid #ced4da;padding:6px 8px;">${escapeHtml(dataPrevista)}</td>
        <td style="border:1px solid #ced4da;padding:6px 8px;">${escapeHtml(prazo)}</td>
        <td style="border:1px solid #ced4da;padding:6px 8px;">${escapeHtml(pctLabel)}</td>
        <td style="border:1px solid #ced4da;padding:6px 8px;text-align:right;">${escapeHtml(valor)}</td>
      </tr>
    `;
  }).join('');

  return `
    <table style="width:100%;border-collapse:collapse;font-size:13px;">
      <thead>
        <tr>
          <th style="border:1px solid #ced4da;padding:6px 8px;text-align:left;background:#f8f9fa;">Nº</th>
          <th style="border:1px solid #ced4da;padding:6px 8px;text-align:left;background:#f8f9fa;">Descrição</th>
          <th style="border:1px solid #ced4da;padding:6px 8px;text-align:left;background:#f8f9fa;">Data prevista</th>
          <th style="border:1px solid #ced4da;padding:6px 8px;text-align:left;background:#f8f9fa;">Prazo</th>
          <th style="border:1px solid #ced4da;padding:6px 8px;text-align:left;background:#f8f9fa;">%</th>
          <th style="border:1px solid #ced4da;padding:6px 8px;text-align:right;background:#f8f9fa;">Valor</th>
        </tr>
      </thead>
      <tbody>${bodyRows}</tbody>
    </table>
  `;
};

const buildAssinaturasHtml = ({ contratanteLabel, contratadaLabel }) => `
  <div style="margin-top:2rem;page-break-inside:avoid;">
    <h2 style="font-size:16px;font-weight:600;margin:0 0 1rem;">Assinaturas</h2>
    <p style="font-size:13px;margin:0 0 1.5rem;">
      E, por estarem assim justas e contratadas, as partes assinam o presente instrumento.
    </p>
    <p style="font-size:13px;margin:0 0 2rem;">Local e data: _________________________________, _____/_____/__________</p>

    <div style="display:flex;justify-content:space-between;gap:24px;margin-bottom:2.5rem;">
      <div style="flex:1;text-align:center;">
        <div style="border-top:1px solid #212529;margin:48px 8px 8px;"></div>
        <div style="font-size:13px;font-weight:600;">Contratante</div>
        <div style="font-size:12px;color:#495057;">${escapeHtml(contratanteLabel || '—')}</div>
      </div>
      <div style="flex:1;text-align:center;">
        <div style="border-top:1px solid #212529;margin:48px 8px 8px;"></div>
        <div style="font-size:13px;font-weight:600;">Contratado(a)</div>
        <div style="font-size:12px;color:#495057;">${escapeHtml(contratadaLabel || '—')}</div>
      </div>
    </div>

    <div style="display:flex;justify-content:space-between;gap:24px;">
      <div style="flex:1;text-align:center;">
        <div style="border-top:1px solid #212529;margin:48px 8px 8px;"></div>
        <div style="font-size:13px;font-weight:600;">Testemunha 1</div>
        <div style="font-size:12px;color:#495057;">Nome / CPF</div>
      </div>
      <div style="flex:1;text-align:center;">
        <div style="border-top:1px solid #212529;margin:48px 8px 8px;"></div>
        <div style="font-size:13px;font-weight:600;">Testemunha 2</div>
        <div style="font-size:12px;color:#495057;">Nome / CPF</div>
      </div>
    </div>
  </div>
`;

export async function buildMinutaContratoDocumentHtml({
  contrFornData = {},
  compraCotacaoInfo = null,
  selContratantes = [],
  selPfsFornecedor = [],
  selPjsFornecedor = [],
  listaParcelas = [],
  includeEntregaveis = true,
} = {}) {
  const minutaModId = Number(contrFornData.minutaMod_id) || 0;
  if (!minutaModId) {
    throw new Error('Selecione um modelo de minuta.');
  }

  const [blocos, secoes, clausulas, itens, placeholderContext] = await Promise.all([
    getBlocos(),
    getSecoes(),
    getClausulas(),
    getModeloItens(minutaModId),
    buildContrFornPlaceholderContext(contrFornData, {
      selContratantes,
      selPfsFornecedor,
      selPjsFornecedor,
      compraCotacaoInfo,
    }),
  ]);

  const arvore = montarArvoreNumerada(itens || []);
  const titulo = `Minuta — ${contrFornData.contrForn_num || 'Contratação'}`;
  const detalhamento = String(contrFornData.contrForn_detalhamento || '').trim() || '—';
  const showEntregaveis = includeEntregaveis !== false;

  const minutaHtml = (itens || []).length > 0
    ? buildMinutaTreeHtml({
      arvore,
      blocoById: indexById(blocos, 'minutaBloco_id'),
      secById: indexById(secoes, 'minutaSec_id'),
      clausById: indexById(clausulas, 'minutaClaus_id'),
      placeholderContext,
    })
    : '<p style="color:#6c757d;">Nenhum item neste modelo.</p>';

  return `
    <div style="font-family:Arial,Helvetica,sans-serif;color:#212529;padding:24px 28px;box-sizing:border-box;background:#fff;">
      <h1 style="font-size:20px;font-weight:700;margin:0 0 1.25rem;text-align:center;">
        ${escapeHtml(titulo)}
      </h1>
      ${minutaHtml}
      <div style="border-top:1px solid #e6e6e6;margin:1.5rem 0;"></div>
      <h2 style="font-size:16px;font-weight:600;margin:0 0 0.75rem;">Detalhamento do serviço</h2>
      <p style="font-size:14px;line-height:1.5;white-space:pre-wrap;margin:0 0 1.5rem;">${escapeHtml(detalhamento)}</p>
      ${showEntregaveis ? `
      <h2 style="font-size:16px;font-weight:600;margin:0 0 0.75rem;">Entregáveis</h2>
      ${buildEntregaveisTableHtml(listaParcelas)}
      ` : ''}
      ${buildAssinaturasHtml({
        contratanteLabel: placeholderContext?.contratante_label,
        contratadaLabel: placeholderContext?.contratada_label,
      })}
    </div>
  `;
}

const waitForNextFrame = () => new Promise((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(resolve));
});

/** Gera o jsPDF da minuta (sem download). Retorna { pdf, fileName, blob, file }. */
export async function buildMinutaContratoPdfDoc(ctx = {}) {
  const minutaModId = Number(ctx.contrFornData?.minutaMod_id) || 0;
  if (!minutaModId) {
    throw new Error('Selecione um modelo de minuta.');
  }

  let container = null;

  try {
    const html = await buildMinutaContratoDocumentHtml(ctx);

    container = document.createElement('div');
    container.setAttribute('data-minuta-contrato-pdf', '1');
    container.style.cssText = [
      'position:fixed',
      'left:-9999px',
      'top:0',
      `width:${A4_WIDTH_PX}px`,
      'background:#ffffff',
      'z-index:-1',
      'pointer-events:none',
    ].join(';');
    container.innerHTML = html;
    document.body.appendChild(container);
    await waitForNextFrame();

    const canvas = await html2canvas(container, {
      scale: 1.25,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: A4_WIDTH_PX,
    });

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const usableWidthMm = A4_WIDTH_MM - (PAGE_MARGIN_MM * 2);
    const usableHeightMm = A4_HEIGHT_MM - (PAGE_MARGIN_MM * 2);
    const imgWidthMm = usableWidthMm;
    const imgHeightMm = (canvas.height * imgWidthMm) / canvas.width;

    const pageHeightPx = Math.floor(
      (usableHeightMm / imgHeightMm) * canvas.height,
    ) || canvas.height;

    let renderedHeightPx = 0;
    let pageIndex = 0;

    while (renderedHeightPx < canvas.height) {
      const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedHeightPx);
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHeightPx;

      const pageCtx = pageCanvas.getContext('2d');
      pageCtx.fillStyle = '#ffffff';
      pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      pageCtx.drawImage(
        canvas,
        0,
        renderedHeightPx,
        canvas.width,
        sliceHeightPx,
        0,
        0,
        canvas.width,
        sliceHeightPx,
      );

      const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.7);
      const pageImgHeightMm = (sliceHeightPx * imgWidthMm) / canvas.width;

      if (pageIndex > 0) pdf.addPage();
      pdf.addImage(
        pageImgData,
        'JPEG',
        PAGE_MARGIN_MM,
        PAGE_MARGIN_MM,
        imgWidthMm,
        pageImgHeightMm,
      );

      renderedHeightPx += sliceHeightPx;
      pageIndex += 1;
    }

    const safeNum = String(ctx.contrFornData?.contrForn_num || 'minuta')
      .replace(/[^\w.-]+/g, '_');
    const fileName = `minuta_${safeNum}.pdf`;
    const blob = pdf.output('blob');
    const file = new File([blob], fileName, { type: 'application/pdf' });
    file.path = fileName;

    return { pdf, fileName, blob, file };
  } finally {
    if (container?.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
}

export async function generateAndDownloadMinutaContratoPdf(ctx = {}) {
  const minutaModId = Number(ctx.contrFornData?.minutaMod_id) || 0;
  if (!minutaModId) {
    notifyError('Selecione um modelo de minuta.');
    return false;
  }

  try {
    const { pdf, fileName } = await buildMinutaContratoPdfDoc(ctx);
    pdf.save(fileName);
    return true;
  } catch (error) {
    console.error('Erro ao gerar PDF da minuta:', error);
    notifyError(error?.message || 'Não foi possível gerar o PDF da minuta.');
    return false;
  }
}
